import { Readability } from '@mozilla/readability';
import { extractArticleFromDocument, minArticleChars } from '@shared/articleExtractor.js';

/**
 * Browser-side Article Fetch
 *
 * A fallback for publishers whose CDN refuses our server. Akamai answers 403
 * to every request from the backend's datacenter IP range regardless of
 * headers, but the same page loads fine from a user's home connection - so
 * when the backend reports it could not fetch, we try from here and post the
 * extracted article back.
 *
 * The extraction itself is the same module the backend runs, so a fix to the
 * heuristics benefits both paths.
 */

/**
 * Publishers have to opt into being read cross-origin by sending
 * Access-Control-Allow-Origin. Roughly half do. The browser gives us a bare
 * TypeError when they do not, indistinguishable from the network being down,
 * so this reason is reported as a possibility rather than a certainty.
 */
export const clientFetchFailureReasons = {
  cors: 'cors',
  http: 'http',
  extraction: 'extraction',
  mixedContent: 'mixed-content',
  // Only reachable from the pasted-source path: what arrived was not a page
  notHtml: 'not-html',
};

const clientFetchTimeoutMs = 30000;

/**
 * Error carrying why the browser attempt failed, so the caller can decide
 * what to tell the user
 */
const fetchError = (message, reason, status = null) => {
  const err = new Error(message);
  err.reason = reason;
  if (status) err.status = status;
  return err;
};

/**
 * Parse page HTML and run the shared extractor over it.
 *
 * `url` becomes the document's base. Readability resolves relative image and
 * link URLs against the document's base URI, which for a parsed string is the
 * panel's own origin - without this, body images come back pointing at
 * easypanel.inshortsapp.com.
 */
const extractFromHtml = (html, url) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const base = doc.createElement('base');
  base.href = url;
  doc.head.prepend(base);

  return extractArticleFromDocument(doc, {
    Readability,
    parseHtml: (fragment) => parser.parseFromString(fragment, 'text/html'),
  });
};

/**
 * Fetch an article in the browser and extract it.
 *
 * Returns `{ title, content, excerpt, imageUrl }`, shaped to match what the
 * backend's own fetcher produces. Throws an error tagged with `reason`.
 */
export const fetchArticleInBrowser = async (url) => {
  // The panel is served over HTTPS, so the browser refuses a plain-HTTP
  // subresource outright. Worth naming, because the generic failure below
  // would blame the publisher for something the browser decided.
  if (window.location.protocol === 'https:' && url.startsWith('http://')) {
    throw fetchError(
      'Your browser will not load an insecure http:// page from the panel - try the https:// address',
      clientFetchFailureReasons.mixedContent
    );
  }

  let response;
  try {
    response = await fetch(url, {
      // Deliberately no custom headers: the browser forbids setting
      // User-Agent and friends, and its own values are more convincing than
      // anything we could put there anyway. Sending none also keeps the
      // request "simple" so it skips the CORS preflight.
      credentials: 'omit',
      redirect: 'follow',
      signal: AbortSignal.timeout(clientFetchTimeoutMs),
    });
  } catch (err) {
    // A CORS rejection and a dead network look identical from script, so say
    // what is most likely without asserting it
    throw fetchError(
      err.name === 'TimeoutError'
        ? 'The publisher did not respond in time'
        : 'This publisher does not allow the browser to read its pages directly',
      clientFetchFailureReasons.cors
    );
  }

  if (!response.ok) {
    throw fetchError(
      `The publisher returned HTTP ${response.status}`,
      clientFetchFailureReasons.http,
      response.status
    );
  }

  const html = await response.text();
  const article = extractFromHtml(html, url);

  if (article.content.length < minArticleChars) {
    throw fetchError(
      `Could not read the article from this page (got ${article.content.length} characters)`,
      clientFetchFailureReasons.extraction
    );
  }

  return article;
};

/**
 * Extract an article out of page source the user pasted in by hand.
 *
 * The last resort once neither route worked: the server is refused, the
 * browser's own fetch is refused too (usually no CORS header), but the editor
 * can always open the page in a tab, copy View page source, and paste it here.
 *
 * Same extraction as every other path, so the result is shaped identically -
 * the only difference is that the markup came through a human. Synchronous:
 * nothing is fetched. Throws an error tagged with `reason`, like the fetch
 * above.
 */
export const extractArticleFromPageSource = (pageSource, url) => {
  const html = (pageSource || '').trim();

  if (!html) {
    throw fetchError('Paste the page source first', clientFetchFailureReasons.notHtml);
  }

  // Page source always carries markup. Saying so here keeps the message about
  // what actually went wrong - a copy of the article's *visible* text extracts
  // to almost nothing, which would otherwise read as the page being unreadable.
  if (!html.includes('<')) {
    throw fetchError(
      'That does not look like page source - use View page source and copy the whole page',
      clientFetchFailureReasons.notHtml
    );
  }

  const article = extractFromHtml(html, url);

  if (article.content.length < minArticleChars) {
    // Two different mistakes land here and the fix differs, so name both: a
    // partial copy, or a page that builds its text in the browser - whose View
    // Source carries a JS shell and no article at all, while the rendered DOM
    // (Inspect, copy outerHTML) carries the whole thing.
    throw fetchError(
      `Could not read the article out of this page source (got ${article.content.length} characters). Check the whole page was copied - if this page builds its text in the browser, copy the rendered HTML from Inspect instead.`,
      clientFetchFailureReasons.extraction
    );
  }

  return article;
};
